import { useFeatureValue } from '@growthbook/growthbook-react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import useIsShoppingInPerson from './useIsShoppingInPerson';
import { logInternalError } from '../lib/errors';
import { getOperatingSystem } from '../lib/util';
import * as types from '../types';
import { useCartPricing } from '../contexts/CartPricingContext';
import { FiCountry, useLocale } from '../contexts/LocaleContext';
import useApplePayRecurring from './useApplePayRecurring';

let memoSupportsApplePay: boolean | null = null;

export function osSupportsApplePay() {
  const os = getOperatingSystem(window);

  // If there is no OS, let's err on the side of caution and
  // show Apple Pay rather than hide it
  if (!os.name) {
    return true;
  }

  return /(Mac|iOS)/i.test(os.name);
}

export function browserSupportsApplePay() {
  if (memoSupportsApplePay !== null) {
    return memoSupportsApplePay;
  }

  // https://developer.apple.com/documentation/apple_pay_on_the_web/apple_pay_js_api/checking_for_apple_pay_availability
  // Memoizing this since it apparently takes up to ~500ms to compute
  // as of 6/18/16 (perf regression on Apple's end?)
  try {
    const applePaySession = (window as any).ApplePaySession;
    memoSupportsApplePay = !!(applePaySession && applePaySession.canMakePayments());
  } catch (exception) {
    // If we run into an error while trying to show apple pay, log it and abort.
    logInternalError(exception);
    memoSupportsApplePay = false;
  }

  return memoSupportsApplePay;
}

/** Reads the Apple Pay feature flag from the env var, with a URL param override for testing. */
function useApplePayFeatureFlag(): boolean {
  const { search } = useLocation();
  const urlParam = new URLSearchParams(search).get('applePayEnabled');
  if (urlParam === 'true') return true;
  if (urlParam === 'false') return false;
  return process.env.REACT_APP_APPLE_PAY_ENABLED !== 'false';
}

export default function useShouldShowApplePay(): boolean {
  // Apple Pay recurringPaymentRequest
  const cartPricing = useCartPricing();

  // When we enable the recurringPaymentRequest, we need to hide Apple Pay when the user is purchasing multiple
  // subscriptions with different billing cadences. This is because the recurringPaymentRequest only supports
  // specifying one billing cadence.
  const billingCadences = cartPricing.totalRecurringCharges?.length;
  const hasMultipleBillingCadences = billingCadences && billingCadences > 1;
  const useRecurringPaymentRequest = useApplePayRecurring();
  const hideApplePayForMultipleBillingCadences = hasMultipleBillingCadences && useRecurringPaymentRequest;

  const { country } = useLocale();
  const applePayCanadaEnabled = useFeatureValue('ecom-apple-pay-canada', true);
  const hideApplePayForCanada = country === FiCountry.CA && !applePayCanadaEnabled;

  const applePayFeatureFlag = useApplePayFeatureFlag();
  const isShoppingInPerson = useIsShoppingInPerson();
  return useSelector(
    (state: types.AppState) =>
      !!(
        applePayFeatureFlag &&
        !hideApplePayForCanada &&
        !hideApplePayForMultipleBillingCadences &&
        !isShoppingInPerson &&
        state.config.siteConfig.showApplePay &&
        osSupportsApplePay()
      ),
  );
}
